import type { baloot, spades } from "@app/rules";
import { useT } from "../i18n/context";
import { send } from "../ws/singleton";
import { Card, rankLabel, SUIT_GLYPH } from "./Card";
import { Key, OptionTile, Sheet } from "./Sheet";

type Suit = spades.SpadesView["yourHand"][number]["suit"];
type WireCard = spades.SpadesView["yourHand"][number];

const isRed = (suit: Suit) => suit === "hearts" || suit === "diamonds";

function callKey(call: baloot.Call) {
  return () => send({ type: "move", move: { kind: "call", call } });
}

/** A suit glyph in its colour, for the tiles. */
function SuitGlyph({ suit }: { suit: Suit }) {
  return <span className={isRed(suit) ? "text-suit-red" : "text-ink"}>{SUIT_GLYPH[suit]}</span>;
}

/** The turned card as a small white chip, like the trump chip in the top bar. */
function TurnedChip({ card }: { card: WireCard }) {
  return (
    <span
      className={`flex h-[26px] flex-none items-center rounded-[6px] bg-card px-1.5 text-[14px] font-extrabold leading-none ${isRed(card.suit) ? "text-card-red" : "text-card-black"}`}
    >
      {rankLabel(card.rank)}
      {SUIT_GLYPH[card.suit]}
    </span>
  );
}

/** The turned card, face up in the middle of the table while the call goes round (everyone sees it). */
export function TurnedCard({
  view,
  card,
}: {
  view: baloot.BalootView;
  card: { w: number; h: number };
}) {
  const { t } = useT();
  if (view.phase !== "bidding" || view.bidding === null) return null;
  return (
    <div className="flex h-full flex-col items-center justify-center gap-1.5">
      <span className="text-legible text-[11px] font-bold uppercase tracking-[0.12em] text-ink/80">
        {t("baloot.call.turned")}
      </span>
      <Card card={view.bidding.turned} width={card.w} height={card.h} state="plain" />
    </div>
  );
}

/** What the footer says: the rule of this moment of the call. */
function callNote(bidding: NonNullable<baloot.BalootView["bidding"]>) {
  if (bidding.call !== null) return "baloot.call.note.over" as const;
  return bidding.round === 1 ? ("baloot.call.note.1" as const) : ("baloot.call.note.2" as const);
}

/**
 * Baloot's call, laid out like the bid and trump panels: title and the turned card
 * above, equal tiles for Hokum in a suit and Sun, the rule on the left and Pass on the right.
 */
export function BalootCallSheet({
  view,
  narrow = false,
}: {
  view: baloot.BalootView;
  narrow?: boolean;
}) {
  const { t } = useT();
  const calls = view.validCalls;
  const bidding = view.bidding;
  if (calls === null || bidding === null) return null;
  const standing = bidding.call;
  const where = standing
    ? t("baloot.call.standing", { suit: t(`suit.${standing.trump ?? "spades"}` as "suit.spades") })
    : t("baloot.call.round", { round: bidding.round });
  const count = calls.hokum.length + Number(calls.sun);
  const aside = (
    <span className="flex items-center gap-2">
      {where}
      <TurnedChip card={bidding.turned} />
    </span>
  );
  return (
    <Sheet title={t("baloot.call.title")} aside={aside} labelId="bid-title" dense={narrow}>
      {count > 0 ? (
        <div
          className="grid gap-2.5"
          style={{
            gridTemplateColumns: `repeat(${narrow ? Math.min(2, count) : count}, minmax(0, 1fr))`,
          }}
        >
          {calls.hokum.map((suit) => (
            <OptionTile
              key={suit}
              glyph={<SuitGlyph suit={suit} />}
              label={t("baloot.call.hokum")}
              hint={t(`suit.${suit}`)}
              name={`${t("baloot.call.hokum")} ${t(`suit.${suit}`)}`}
              dense={narrow}
              onClick={callKey({ kind: "hokum", suit })}
            />
          ))}
          {calls.sun ? (
            <OptionTile
              glyph={<span className="text-gold">☀︎</span>}
              label={t("baloot.call.sun")}
              hint={t("baloot.call.noTrump")}
              name={t("baloot.call.sun")}
              dense={narrow}
              onClick={callKey({ kind: "sun" })}
            />
          ) : null}
        </div>
      ) : null}
      <div className="flex items-center justify-between gap-3">
        <span className="text-[12px] text-ink/55">{t(callNote(bidding))}</span>
        {calls.pass ? (
          <Key
            label={t("table.bid.pass")}
            tone="pass"
            dense
            className="flex-none rounded-pill px-7 text-[14px]"
            onClick={callKey({ kind: "pass" })}
          />
        ) : null}
      </div>
    </Sheet>
  );
}

/** Doubling: what a raise means, then Pass and the raise side by side, equal (like the match-end buttons). */
export function BalootDoubleSheet({
  view,
  narrow = false,
}: {
  view: baloot.BalootView;
  narrow?: boolean;
}) {
  const { t } = useT();
  const doubles = view.validDoubles;
  const doubling = view.doubling;
  if (doubles === null || doubling === null) return null;
  const suit = doubling.pending.trump ?? "spades";
  const aside = `${t("baloot.call.hokum")} ${SUIT_GLYPH[suit]} · ${t(`baloot.double.${doubling.level}` as "baloot.double.1")}`;
  const raise = doubles.raise;
  return (
    <Sheet title={t("baloot.double.title")} aside={aside} labelId="bid-title" dense={narrow}>
      <p className="text-[13px] text-ink/70">
        {raise === null ? t("baloot.double.top") : t("baloot.double.note", { times: raise })}
      </p>
      <div className={`grid gap-3 ${raise === null ? "grid-cols-1" : "grid-cols-2"}`}>
        <Key
          label={t("table.bid.pass")}
          tone="pass"
          className="rounded-pill text-[15px]"
          onClick={() => send({ type: "move", move: { kind: "double", raise: false } })}
        />
        {raise !== null ? (
          <Key
            label={t(`baloot.double.${raise}` as "baloot.double.2")}
            tone="gold"
            className="rounded-pill text-[15px]"
            onClick={() => send({ type: "move", move: { kind: "double", raise: true } })}
          />
        ) : null}
      </div>
    </Sheet>
  );
}
